<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:settings-2',
	name: '$t:blocks.input_slider.name',
	description: '$t:blocks.input_slider.description',
	category: 'input',
	tags: ['form-control'],
	fieldBinding: {
		supportedTypes: ({ primitiveType }) => {
			if (isPrimitiveTypeDecimal(primitiveType)) return [];

			return ['number'];
		},
	},

	options: [
		{
			name: 'modelValue',
			label: '$t:blocks.common.props.value',
			model: true,
		},
		{
			name: 'min',
			label: '$t:blocks.common.props.min',
		},
		{
			name: 'max',
			label: '$t:blocks.common.props.max',
		},
		{
			name: 'step',
			label: '$t:blocks.common.props.step',
		},
		{
			name: 'color',
			label: '$t:blocks.common.props.color',
			block: BuiltinBlockIds.InputSemanticColor,
		},
		{
			name: 'size',
			label: '$t:blocks.common.props.size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'block',
			label: '$t:blocks.common.props.block',
		},
		{
			name: 'disabled',
			label: '$t:blocks.common.props.disabled',
		},
		{
			name: 'disabledNotice',
			label: '$t:blocks.common.props.disabled_notice',
		},
		{
			name: 'readonly',
			label: '$t:blocks.common.props.readonly',
		},
	],
}
</script>
<script lang="ts" setup>
import type { SliderProps } from '@nuxt/ui';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { NumberInputBounds } from '#layers/data-model/public';
import { reactivePick } from '@vueuse/core';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { computed } from 'vue';
import { defineBlock } from '#layers/block-engine/public';
import { BLOCK_CONTROL_SIZE_OPTIONS } from '#layers/blocks/constants/ui-options';
import { isPrimitiveTypeDecimal } from '#layers/data-model/public';
import { useInteger64FormFieldInput, useNumberFormFieldModel, usePrimitiveFormField } from '#layers/forms/public';
import { useDisabledNotice, useReadonly } from '#layers/ui/public';

interface Props extends
	NumberInputBounds,
	Pick<InputBlockProps, 'disabled' | 'disabledNotice' | 'readonly'>,
	Pick<SliderProps, 'color' | 'size'> {
	/**
	 * Render the input full width
	 */
	block?: boolean;
}

const {
	min,
	max,
	step,
	readonly,
	disabled: disabledProp,
	disabledNotice: disabledNoticeProp,
	...forwardedProps
} = defineProps<Props>();
const model = defineModel<number | string>();

;

const forwarded = computed(() => ({
	...reactivePick(forwardedProps, 'color', 'size'),
	min: min !== undefined ? Number(min) : undefined,
	max: max !== undefined ? Number(max) : undefined,
	step: step !== undefined ? Number(step) : undefined,
}));
const { modelValue, disabled, disabledNotice, field } = usePrimitiveFormField(() => ({ disabled: disabledProp, disabledNotice: disabledNoticeProp }), { model });

const sliderModel = useNumberFormFieldModel(modelValue, field);

// A slider position is a JS number, which only holds a 64-bit value exactly within the safe-integer
// range. Outside it, the field is edited as exact decimal text instead.
const { inputProps: integer64Props, inputModel: integer64Model } = useInteger64FormFieldInput(modelValue, field, () => ({ ...forwardedProps, min, max, step, readonly }));
const showSlider = computed(() => !integer64Props.value || (isSafeInteger(min) && isSafeInteger(max) && (modelValue.value == null || isSafeInteger(modelValue.value))));

function isSafeInteger(value: unknown): boolean {
	return (typeof value === 'number' || typeof value === 'string') && value !== '' && Number.isSafeInteger(Number(value));
}

const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => readonly);
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice">
		<DSlider
			v-if="showSlider"
			v-model="sliderModel"
			v-bind="forwarded"
			tooltip
			:disabled
			:aria-readonly="readonly"
			:class="{
				// `block` widens, it must not grow: reka binds pointerdown on the slider root, so a root
				// stretched over a taller form row turns that whole blank area into a value-setting target.
				'w-full': block,
				'pointer-events-none': readonly,
			}"
			@pointerdown.capture="preventPointer"
			@click.capture="preventPointer"
			@keydown.capture="preventKeyboardNavigation"
		/>
		<DInputDecimalNumber
			v-else
			v-bind="integer64Props"
			v-model="integer64Model"
			:size
			:readonly
			:disabled
			:aria-readonly="readonly"
			class="min-w-0"
			:class="{ 'w-full': block }"
		/>
	</DTooltip>
</template>
